import { useQuery } from '@tanstack/react-query'
import {
  fromDownlineAccountsData,
  getDownlineAccountsData,
  getDownlineAccountsSumOnly,
  liveBody,
  netFromDownlineSumOnly,
  plStatementRangeError,
  unavailablePlStatement,
  type PlStatementQuery,
  type PlStatementResult,
} from '@/api/map/plStatement'
import { keys } from '@/api/hooks/shared/keys'

export { canLoadSumOnly, liveBody, plStatementRangeError } from '@/api/map/plStatement'

const DEFAULT_PAGE_SIZE = 20

/** Filter body for SumOnly — same Lab fields as the table, without page. */
export function sumOnlyBody(query: PlStatementQuery) {
  const body = liveBody(query, 1, DEFAULT_PAGE_SIZE)
  delete body.page
  delete body.pageSize
  return { ...body, includeCount: true }
}

export async function loadPlStatement(query: PlStatementQuery): Promise<PlStatementResult> {
  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = query.page ?? 1

  const rangeErr = plStatementRangeError(query.range)
  if (rangeErr) {
    return unavailablePlStatement({ ...query, page, pageSize }, rangeErr)
  }

  try {
    const body = liveBody(query, page, pageSize)
    const live = await getDownlineAccountsData(body)
    return fromDownlineAccountsData(live, { ...query, page, pageSize })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Request failed'
    return unavailablePlStatement({ ...query, page, pageSize }, message)
  }
}

/**
 * Lab Net total amount — `POST /v1/account/getDownlineAccountsSumOnly`.
 * Today → PG; historical → BQ (Lab Mongo vs BigQuery). Cap 93 days.
 */
export async function loadPlStatementSumOnly(
  query: PlStatementQuery,
): Promise<{ netPnl: number; totalRecords?: number } | null> {
  if (plStatementRangeError(query.range)) return null
  try {
    const response = await getDownlineAccountsSumOnly(sumOnlyBody(query))
    const netPnl = netFromDownlineSumOnly(response)
    if (netPnl === undefined) return null
    const totalRecords = response.value ? response.data?.totalRecords : undefined
    return {
      netPnl,
      ...(typeof totalRecords === 'number' ? { totalRecords } : {}),
    }
  } catch {
    return null
  }
}

export function usePlStatement(query: PlStatementQuery) {
  return useQuery({
    queryKey: keys.plStatement(query),
    queryFn: () => loadPlStatement(query),
    placeholderData: (previous) => previous,
  })
}

export function usePlStatementSumOnly(query: PlStatementQuery) {
  const sumKey = {
    from: query.range.from.slice(0, 10),
    to: query.range.to.slice(0, 10),
    ...(query.q?.trim() ? { q: query.q.trim() } : {}),
    ...(query.tms ? { tms: query.tms } : {}),
    ...(query.name?._id ? { nameId: query.name._id } : {}),
    ...(query.game && query.game !== 'all' ? { game: query.game } : {}),
    ...(query.subGame ? { subGame: query.subGame } : {}),
    ...(query.eventName ? { eventName: query.eventName } : {}),
    ...(query.typeName ? { typeName: query.typeName } : {}),
    ...(query.bookmakerSessionFlag && query.bookmakerSessionFlag !== 'all'
      ? { bookmakerSessionFlag: query.bookmakerSessionFlag }
      : {}),
    ...(query.memberIds?.length ? { memberIds: query.memberIds } : {}),
  }
  return useQuery({
    queryKey: keys.plStatementSumOnly(sumKey),
    queryFn: () => loadPlStatementSumOnly(query),
    // Fire on mount (page load) and filter changes; pager is not in sumKey.
    placeholderData: (previous) => previous,
  })
}
